import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatCurrency } from '../../../utils/formatUtils';
import { DASHBOARD_CHART_COLORS } from '../constants/accountingEntriesConstants';
import useAccountingDashboardData from '../hooks/useAccountingDashboardData';
import {
  formatCompactAmount,
  getDashboardChartColor,
  truncateChartLabel,
} from '../utils/accountingEntriesUtils';
import { DashboardChartCard, DashboardMetric } from '../../../common/components';

const AccountingDashboardTab = ({ entries, ledgerRows, loading }) => {
  const theme = useTheme();
  const {
    accountsWithBalanceCount,
    balanceAccounts,
    balancedRatio,
    difference,
    monthlyData,
    originData,
    statusData,
    topMovementAccounts,
    totals,
  } = useAccountingDashboardData(entries, ledgerRows);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, backgroundColor: 'background.paper' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
          gap: 1.25,
        }}
      >
        <DashboardMetric label="Debe total" value={formatCurrency(totals.debit, 2)} helper={`${entries.length} asientos`} />
        <DashboardMetric label="Haber total" value={formatCurrency(totals.credit, 2)} helper={`${balancedRatio.toLocaleString('es-AR', { maximumFractionDigits: 1 })}% balanceados`} />
        <DashboardMetric label="Diferencia" value={formatCurrency(Math.abs(difference), 2)} helper={difference === 0 ? 'Debe y Haber balancean' : 'Revisar asientos'} />
        <DashboardMetric label="Contabilizados" value={totals.posted.toLocaleString('es-AR')} helper={`${totals.draft.toLocaleString('es-AR')} borradores`} />
        <DashboardMetric label="Cuentas con saldo" value={accountsWithBalanceCount.toLocaleString('es-AR')} helper="Mayor contable" />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.5,
          alignItems: 'stretch',
        }}
      >
        <DashboardChartCard title="Asientos por mes" loading={loading} hasData={monthlyData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlyData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="label" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => Number(value || 0).toLocaleString('es-AR')} />
              <Line type="monotone" dataKey="asientos" name="Asientos" stroke={DASHBOARD_CHART_COLORS[2]} strokeWidth={3} dot={{ r: 3, fill: DASHBOARD_CHART_COLORS[2] }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Debe y Haber mensual" loading={loading} hasData={monthlyData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="label" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis tickFormatter={formatCompactAmount} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatCurrency(value, 2)} />
              <Legend />
              <Bar dataKey="debe" name="Debe" fill={DASHBOARD_CHART_COLORS[0]} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="haber" name="Haber" fill={DASHBOARD_CHART_COLORS[1]} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Debe y Haber por origen" loading={loading} hasData={originData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={originData} layout="vertical" margin={{ top: 8, right: 20, left: 80, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis type="number" tickFormatter={formatCompactAmount} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={140} tickFormatter={(value) => truncateChartLabel(value, 20)} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatCurrency(value, 2)} />
              <Legend />
              <Bar dataKey="debe" name="Debe" fill={DASHBOARD_CHART_COLORS[4]} radius={[0, 4, 4, 0]} isAnimationActive={false} />
              <Bar dataKey="haber" name="Haber" fill={DASHBOARD_CHART_COLORS[5]} radius={[0, 4, 4, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Cuentas con mayor movimiento" loading={loading} hasData={topMovementAccounts.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topMovementAccounts} layout="vertical" margin={{ top: 8, right: 20, left: 90, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis type="number" tickFormatter={formatCompactAmount} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={150} tickFormatter={(value) => truncateChartLabel(value, 22)} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatCurrency(value, 2)} />
              <Bar dataKey="value" name="Movimiento" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {topMovementAccounts.map((entry, index) => (
                  <Cell key={entry.name} fill={getDashboardChartColor(index)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Saldos por cuenta" loading={loading} hasData={balanceAccounts.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={balanceAccounts} layout="vertical" margin={{ top: 8, right: 20, left: 90, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis type="number" tickFormatter={formatCompactAmount} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={150} tickFormatter={(value) => truncateChartLabel(value, 22)} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatCurrency(value, 2)} />
              <Bar dataKey="balance" name="Saldo" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {balanceAccounts.map((entry, index) => (
                  <Cell key={entry.name} fill={getDashboardChartColor(index + 3)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Distribución por estado" loading={loading} hasData={statusData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={90} paddingAngle={2} isAnimationActive={false}>
                {statusData.map((entry, index) => (
                  <Cell key={entry.name} fill={getDashboardChartColor(index + 6)} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value) => Number(value || 0).toLocaleString('es-AR')} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </DashboardChartCard>
      </Box>
    </Box>
  );
};

export default AccountingDashboardTab;
